import { Component, EventEmitter, Input, OnChanges, OnInit, Output, OnDestroy } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AddEditRowFormStateOption, ColumnDefinition, DynamicTableData } from '../../models/dynamic-table/dynamic-table-data.model';
import { DynamicTableService } from '../../services/dynamic-table/dynamic-table.service';
import { AppMainService } from 'src/app/app-main.service';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { catchError, Observable, of, Subject, takeUntil } from 'rxjs';
import { TimeZoneService } from '../../services/timezone.service';
import { AppMainComponent } from 'src/app/app.main.component';
import { DocumentManagementService } from '../../services/document-management/document-management.service';
import { validateFiles, validateFileSize } from 'src/app/shared/upload-files';
import { FormControlListIds } from '../../constants/formcontrol-list.constants';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DocumentViewerService } from '../../services/document-viewer.service';
import { WBPermissionType } from 'src/app/misc/workspace/enum/workbook-permission.enum';
import { WorkbookStateService } from '../../services/workbook-state.service';


@Component({
  selector: 'app-dynamic-table-add-edit-form',
  templateUrl: './dynamic-table-add-edit-form.component.html',
  styleUrl: './dynamic-table-add-edit-form.component.scss'
})
export class DynamicTableAddEditFormComponent implements OnInit, OnDestroy {

  @Input() columnDefinitions: ColumnDefinition[];
  @Input() mode: 'add' | 'edit' | 'view' = 'add';
  @Input() displayModal: boolean;
  @Input() formHeader: string;
  @Input() rowNumber: number;
  @Input() dynamicTableData: DynamicTableData;
  @Input() dataTypesValidation?:any
  @Input() permissionType?: WBPermissionType= WBPermissionType.Read;
  @Input() columnId: string;
  @Input() columnName: string;
  @Output() closeAddEditModal = new EventEmitter<void>();
  @Output() formStatusChange? = new EventEmitter<{ isUpdate: boolean }>();
  WBPermissionType=WBPermissionType;

multiLineTooltipPicture = `<ul class="p-mt-0 p-pl-4 p-mb-0">
  <li>Files with extensions: gif, png, jpg, jpeg are allowed</li>
  <li>Files size should not be more than 5MB</li>
</ul>`;
multiLineTooltipFile = `<ul class="p-mt-0 p-pl-4 p-mb-0">
<li>Files with extensions: gif, png, jpg, jpeg, pdf, txt, xls, xlsx, doc, docx, ppt, pptx are allowed</li>
<li>Files size should not be more than 5MB</li>
</ul>`;
// uploadedFiles = [];
// uploadedPictures = [];

  stateOptions:AddEditRowFormStateOption[] = []
  selectedOption: string;
  /** Subscription and Cleanup */
  protected ngUnsubscribe$: Subject<void> = new Subject<void>();

  // Control the visibility of the modal

  form: FormGroup;
  isFormChanged: boolean = false;
  usersList: any;
  excludedFields = ['DatePicker', 'DateTimePicker', 'Percentage', 'Currency', 'Phone', 'RichText', 'EncryptedText', 'Time', 'URL', 'FileUpload', 'Picture', 'Barcode', 'UserLookup', 'CheckBox', 'Multi-Line', 'Decimal'];
  allowedExtensions = ['.png', '.jpg', '.jpeg', '.gif'];
  workbookRowData: any;

  //user details
  displayUserDetails = false;
  selectedUser: any;
  currencySymbol: string = "";

  //files and pics
  allFiles = [];
  displayFiles = false;
  items: MenuItem[] | undefined;
  displayExportConfirm = false;
  deletedDocuments: any[] = [];

  constructor(
    public dynamicTableService: DynamicTableService,
    private readonly appMainService: AppMainService,
    private readonly messageService: MessageService,
    private readonly fb: FormBuilder,
    private readonly timezoneService: TimeZoneService,
    private app: AppMainComponent,
    private readonly documentManagementService: DocumentManagementService,
    private readonly confirmationService: ConfirmationService,
    private sanitizer: DomSanitizer,
    private documentViewerService: DocumentViewerService,
    private workbookStateService: WorkbookStateService
  ) {
    this.usersList = this.app.usersList;
    // this.usersList = users.map(user => ({ id: user.id, value: user.name }));
    this.items = [
      {
        label: 'Export',
        command: () => {
          this.displayExportConfirm = true;
        },
      },
      {
          label: 'Print',
      },
      {
        label: 'Email',
      },
    ];
  }

  ngOnInit() {
    this.initializePicklistValues();
    if (this.mode === 'edit' || this.mode === 'view') {
      this.getWorkbookRow(this.dynamicTableData.id, this.rowNumber);
    }
    this.buildForm();
    this.updateStateOptions();
  }

  onCloseExportHide(event) {
    this.displayExportConfirm = false;
  }

  getWorkbookRow(tableId: string, rowNumber: number) {
    this.appMainService.isShowSpinner('getWorkbookRow');
    this.dynamicTableService.getWorkbookRow(tableId, rowNumber).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (response: any[]) => {
        this.appMainService.isHideSpinner('getWorkbookRow');
        this.workbookRowData = response;
        if (this.mode === 'edit' || this.mode === 'view') {
          // this.buildForm();
            this.form.reset(); // Reset the form before patching new data
          this.patchFormData();
        }
      },
      error: (error) => {
        this.appMainService.isHideSpinner('getWorkbookRow');
      }
    })
  }

  initializePicklistValues() {
    this.columnDefinitions.forEach(column => {
      if (column.formControlName === 'MultipleDropDownList') {
        this.getWorkbookColumnOptionValue(column.id).subscribe((pickListValues) => {
          column.pickListValues = pickListValues?.map(item => item?.value) ||[];  // Assign API response to pickListValues
        });
      }
    });
  }

  buildForm() {
    const group: any = {};
    this.columnDefinitions.forEach(column => {
      const control = this.createControl(column);
      group[column.id] = control;
    });

    this.form = this.fb.group(group);

    // Subscribe to form value changes to track changes
    this.form.valueChanges.subscribe(() => {
      this.isFormChanged = !this.form.pristine;
      // this.isFormChanged = true;
    });
  }

  createControl(column: any) {
    const validators = [];
    const validation = this.getValidationRules(column.formControlId);

    // if (validation?.isRequired) {
    //   validators.push(Validators.required);
    // }
    if(validation?.validationRegex && [FormControlListIds.Email,FormControlListIds.Phone,FormControlListIds.IPAddress,FormControlListIds.URL].includes(column.formControlId)){
      validators.push(Validators.pattern(validation?.validationRegex));
    }

    if (column.formControlName === 'RadioButton') {
      return this.fb.control(null, validators); // Single selection
    }
    else if (column.formControlName === 'FileUpload') {
      return this.fb.array([]); // Store uploaded file responses
    }
    else if (column.formControlName === 'Picture') {
      return this.fb.array([]); // Store uploaded file responses
    }
    else if (column.formControlName === 'CheckBox') {
      // Return a FormArray with one FormControl for each option, initialized to false
      return new FormArray(
        column.pickListValues.map(() => new FormControl(null))
      );
    }
    return this.fb.control({ value: null, disabled: false }, validators);
  }


  onCheckboxChange(columnId: string, option: string) {
    const formArray: FormArray = this.form.get(columnId) as FormArray;
    if (formArray.value.includes(option)) {
      const index = formArray.value.indexOf(option);
      formArray.removeAt(index);
    } else {
      formArray.push(new FormControl(option));
    }
  }


  getValidationRules(formControlId: string) {
    return this.dataTypesValidation.find(rule => rule.id === formControlId);
  }

  patchFormData() {
    if (this.columnDefinitions) {
      const patchedData: any = {};

      this.columnDefinitions.forEach(column => {
        // const fieldValue = this.formData[column.id];
        const tempFieldValue = this.workbookRowData?.find(cell => cell.columnId === column.id)?.cellValue;
        const fieldValue = [FormControlListIds.MultipleDropDownList, FormControlListIds.FileUpload, FormControlListIds.Picture,FormControlListIds.CheckBox, FormControlListIds.UserLookup].includes(column?.formControlId)&& tempFieldValue? this.safeJsonParse(tempFieldValue): tempFieldValue;
        if ((column.formControlName === 'DatePicker') && fieldValue) {
           patchedData[column.id] = this.timezoneService.showDateTimeWithZone(fieldValue, false);
        }
        else if((column.formControlName === 'DateTimePicker') && fieldValue){
          patchedData[column.id] = this.timezoneService.showDateTimeWithZone(fieldValue, false);
        }
        else if((column.formControlName === 'UserLookup') && fieldValue){
          const selectedUser = this.usersList.find(u => u?.id == fieldValue?.UserId);
          patchedData[column.id] = selectedUser?.id;
        }
        else if ((column.formControlName === 'AutoNumber') && fieldValue) {
          patchedData[column.id] = fieldValue;
        }
        else if ((column.formControlName === 'ToggleButton') && fieldValue) {
          patchedData[column.id] =  this.toBoolean(fieldValue);
        }
        else if((column.formControlName === 'FileUpload' || column.formControlName === 'Picture') && fieldValue?.length>0){
          this.updateFormWithFiles(fieldValue, column.id)
        }
        else if (column.formControlName === 'CheckBox' && fieldValue) {
          let formArray = this.form.get(column.id) as FormArray;

          if (!formArray) {
            this.form.setControl(column.id, new FormArray([]));
            formArray = this.form.get(column.id) as FormArray;
          }
          const values = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
          column.pickListValues.forEach((option, index) => {
            const isChecked = values.includes(option);
            formArray.at(index).setValue(isChecked);
          });
        }
         else {
          patchedData[column.id] = fieldValue;
        }
      });

      this.form?.patchValue(patchedData);
    }

  }

  safeJsonParse(value: any): any {
    try {
      return JSON.parse(value);
    } catch (e) {
      return value; // Return as is if parsing fails
    }
  }

   toBoolean(value: any): boolean {
    return value === 'true' || value === true;
  }

  getWorkbookColumnOptionValue(columnId: string, query: string = ''): Observable<any[]> {
    return this.dynamicTableService.getWorkbookColumnOptionValue(columnId, query);
  }

  onDropdownSearch(column: ColumnDefinition, query: string) {
    const control = this.form.get(column.id);
    const searchQuery = query?.trim();
    if (column?.pickListValues?.length > 0 && !searchQuery) {
          column.isLoading = false;
          column.pickListValues = [...column?.pickListValues];
        return; // Return if pickListValues has length but query is falsy
    }
    else{
      column.isLoading = true;
      this.getWorkbookColumnOptionValue(column.id, searchQuery).pipe(
        catchError(error => {
            column.isLoading = false;
            return of([]);
        })
    ).subscribe(data => {
        column.isLoading = false;
        const pickListValues = data.map(item => item?.value);
        column.pickListValues = pickListValues;

        if (!(pickListValues && pickListValues.length > 0)) {
            control?.clearValidators(); // Remove all validators if no pickListValues
        }

        control?.updateValueAndValidity();
    });
    }
  }

  onFieldChange(value: any) {
  }

  onSubmit(str?) {
    if (this.form.valid) {
      if (this.mode === 'edit') {
        const modifiedRowObject = this.mapSubmitData(this.form.value, this.rowNumber);
        this.updateWorkbookRow(modifiedRowObject,str);
      }
      else if (this.mode === 'add') {
        const newRowObject = this.mapNewRowData(this.form.value);
        this.createWorkbookRow(newRowObject, str);
      }
    }else{
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please check the input values!' });
    }
  }

  mapNewRowData(formData: { [key: string]: any }) {
    const cells = this.mapSubmitData(formData);

    return {
      tableId: this.dynamicTableData.id,
      cells: cells.map(cell => ({
        columnId: cell.columnId,
        value: cell.value,
        values: cell.values,
      }))
    };
  }


  createWorkbookRow(newRow: any, str?) {
    this.appMainService.isShowSpinner('createWorkbookRow');
    this.dynamicTableService.createWorkbookRow(this.dynamicTableData.id, newRow).pipe(takeUntil(this.ngUnsubscribe$)).subscribe(
      {
        next: (response: any) => {
          this.appMainService.isHideSpinner('createWorkbookRow');
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Row added successfully!' });
          const isUpdate = true;
          this.formStatusChange.emit({ isUpdate });

          // Notify state change through service
          this.workbookStateService.notifyStateChange({
            type: 'add',
            workbookId: this.dynamicTableData.id,
            data: newRow,
            columnId: this.columnId,
            columnName: this.columnName
          });

          this.processDocumentDeletions();
          this.clearOldValue();
          if(str == "view"){
            this.mode = "view";
            this.getWorkbookRow(this.dynamicTableData.id, this.rowNumber);
          } else {
            this.closeModal();
          }
        },
        error: (error) => {
          this.appMainService.isHideSpinner('createWorkbookRow');
          this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.Message || 'Failed to add Row!' });
          this.closeModal();
        }
      },
    );
  }

  updateWorkbookRow(modifiedRow: any,str?) {
    this.appMainService.isShowSpinner('updateWorkbookRow');
    this.dynamicTableService.updateWorkbookRow(this.dynamicTableData.id, modifiedRow).pipe(takeUntil(this.ngUnsubscribe$)).subscribe(
      {
        next: (response: any) => {
          this.appMainService.isHideSpinner('updateWorkbookRow');
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Row updated successfully!' });
          const isUpdate = true;
          this.formStatusChange.emit({ isUpdate });

          // Notify state change through service
          this.workbookStateService.notifyStateChange({
            type: 'update',
            workbookId: this.dynamicTableData.id,
            data: modifiedRow,
            columnId: this.columnId,
            columnName: this.columnName
          });

          this.processDocumentDeletions();
          this.clearOldValue();
          if(str == "view"){
            this.mode = "view";
            this.getWorkbookRow(this.dynamicTableData.id, this.rowNumber);
          } else {
            this.closeModal();
          }
        },
        error: (error) => {
          this.appMainService.isHideSpinner('updateWorkbookRow');
          this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.Message || 'Failed to update Row!' });
          this.closeModal();
        }
      },
    );

  }

  processDocumentDeletions() {
    if (this.deletedDocuments.length === 0) return;

    // Start with a counter to track completions
    let completedCount = 0;
    const totalToDelete = this.deletedDocuments.length;

    // Process each document to delete
    this.deletedDocuments.forEach(doc => {
      this.documentManagementService.deleteDocument(doc.id).subscribe({
        next: (response: any) => {
          completedCount++;
          if (completedCount === totalToDelete) {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: `${totalToDelete} document(s) deleted successfully!`
            });
            this.deletedDocuments = []; // Clear the list
          }
        },
        error: (error) => {
          completedCount++;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: `Failed to delete document: ${error?.error?.Message || 'Unknown error'}`
          });
          if (completedCount === totalToDelete) {
            this.deletedDocuments = []; // Clear the list even on error
          }
        }
      });
    });
  }

  // Method to switch to edit mode
  switchToEdit() {
    if (this.mode === 'view') {
      this.mode = 'edit';
      this.formHeader = `${this.dynamicTableData.name}: Edit Row ${this.rowNumber}`;
      this.stateOptions = [
        {label:this.formHeader,value:"view-mode"},{label:"Comments",value:"comments"}
      ]
      this.form.enable(); // Enable the form controls for editing
      this.selectedOption = "view-mode";
    }
  }

  mapSubmitData(submitData: { [key: string]: any }, rowNumber?: number) {
    const mappedArray = [];

    for (let [columnId, value] of Object.entries(submitData)) {
      const columnDef = this.columnDefinitions.find(col => col.id === columnId);
      if (columnDef.formControlName === 'DatePicker' && value) {
        value = this.timezoneService._getCustomDateString(value)
      }
      else if (columnDef.formControlName === 'DateTimePicker' && value) {
        value = this.timezoneService._getCustomDateTimeString(value)
      }
      else if (columnDef.formControlName === 'DateTimePicker' && value) {
        value = this.timezoneService._getCustomDateTimeString(value)
      }
      else if (columnDef.formControlName === 'UserLookup' && value) {
        value = value.toString();
      }
      else if (columnDef.formControlName === 'ToggleButton') {
        if (value === true || value === false) {
          value = value.toString() || 'false';
        }else{
          value ='false';
        }
      }
      else if ((columnDef.formControlName === 'FileUpload' || columnDef.formControlName === 'Picture')&& value) {
        value = JSON.stringify(value);
      }
      else if(columnDef.formControlName === 'Formula' || columnDef.formControlName === 'AutoNumber'){
        continue;
      }
      const dataObject: { columnId: string; value?: any; values?: any[]; rowNumber?: number } = {
        columnId
      };

      // Use 'values' instead of 'value' for MultiDropdown and Checkbox
      if (columnDef.formControlName === 'MultipleDropDownList') {
        if (Array.isArray(value)) {
          // Flatten the array and filter out null values
          dataObject.values = value
            .flat()
            .filter(item => item !== null && item !== undefined);
        }
        // If it's a single value (not null)
        else if (value) {
          dataObject.values = [value];
        }
        else {
          dataObject.values = null;
        }
      }
      else if (columnDef.formControlName === 'CheckBox') {
        let formArray = this.form.get(columnDef.id) as FormArray;

        if (formArray) {
          const selectedValues = columnDef.pickListValues.filter(
            (_, index) => formArray.at(index).value === true
          );

          if (selectedValues.length > 0) {
            dataObject.values = selectedValues;
          }
          else {
            dataObject.values = null;
          }
        }
      }

       else {
        dataObject.value = value;
      }

      // Only add rowNumber if it's passed
      if (rowNumber !== undefined) {
        dataObject.rowNumber = rowNumber;
      }
      mappedArray.push(dataObject);
    }
    return mappedArray;
  }

  getCheckBoxValue(column){
    let formArray = this.form.get(column.id) as FormArray;
    const selectedValues = column.pickListValues.filter(
      (_, index) => formArray.at(index).value === true
    );
    return selectedValues;
  }
  onCustomUpload(event: any, field: any, fileUpload, type) {
    if (event?.files&& event?.files?.length>5) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Maximum 5 files are allowed at once!' });
      fileUpload.clear();
      return;
    }
    let errors = [];
    for (let file of event.files) {
      this.appMainService.isShowSpinner('upload')
      if (type == 'image') {
          errors = validateFiles(file, 5, this.allowedExtensions);
      }else{
          errors = validateFileSize(file, 5);
      }

      if (errors.length) {
          for (let error of errors) {
              this.messageService.add({
                  severity: 'error',
                  summary: 'Error',
                  detail: error.fileName + ": " + error.error
              });
          }
          this.appMainService.isHideSpinner('upload');
      }
      this.appMainService.isHideSpinner('upload');    }
      if(errors.length){
        fileUpload.clear();
        return;
      }
    const uploadedFiles:File[] = Array.from(event.files || []); // Convert FileList to an array
    if (!Array.isArray(uploadedFiles)) {
      return;
    }
    const path = `workbook/${this.dynamicTableData.id}/row-${this.rowNumber}`;
    this.bulkUploadDocument(uploadedFiles, path, field.id);
    fileUpload.clear();
  }

  bulkUploadDocument(files: File[], path: string, fieldId) {
    this.appMainService.isShowSpinner('bulkUpload');
    this.documentManagementService.bulkUploadDocument(files, path)
  .pipe(takeUntil(this.ngUnsubscribe$))
  .subscribe({
    next: (response: any) => {
      this.appMainService.isHideSpinner('bulkUpload');
      if (response) {
        this.updateFormWithFiles(response, fieldId);
        this.form.markAsDirty();
        this.form.markAsTouched();
        this.form.updateValueAndValidity();
      }
    },
    error: (error) => {
      this.appMainService.isHideSpinner('bulkUpload');
      this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error?.Message || 'Failed to upload documents!' });
    }
  });

  }

  rawHTML(content: string): SafeHtml{
    return content ?  this.sanitizer.bypassSecurityTrustHtml(content): ''
}

  // Update form control with API response
  updateFormWithFiles(files: any[], fieldId: string) {
    const formArray = this.form.get(fieldId) as FormArray;

    // Remove null or undefined values from the existing FormArray
    const validControls = formArray.controls.filter(control => control.value !== null && control.value !== undefined);
    formArray.clear(); // Clear the existing FormArray
    validControls.forEach(control => formArray.push(control)); // Add back only valid values

    files.forEach(file => {
      if (file !== null && file !== undefined) {
        formArray.push(new FormControl(file));
      }
    });
  }


  downloadFile(file, event, isDownload=false) {
    if(event){
      event.stopPropagation();
    }
    const documentPayload = {
      id: file?.id,
      isDefaultContentType: isDownload? true: false,
      expirationTimeInMinutes: 5
    }
    this.appMainService.isShowSpinner('downloadFile');
    this.documentManagementService.downloadDocument(documentPayload).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (response: any) => {
        this.appMainService.isHideSpinner('downloadFile');
        if(isDownload){
          const fileUrl = response?.downloadLink; // Replace with your API's file URL field
          if (fileUrl) {
            window.open(fileUrl, '_blank'); // Open the file in a new tab
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Document Download successfully!' });
          } else {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'File URL is not available' });
          }
        }
        else {
          const getFileExtension = (filename: string) => filename.split('.').pop() || '';
          const extension = getFileExtension(file?.fileName);
          this.documentViewerService.previewDocument(extension, response);
        }
      },
      error: (error) => {
        this.appMainService.isHideSpinner('downloadFile');
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'File URL is not available' });
      }
    })
  }

  deleteDocument(selectedFile: any, column, index) {
    this.confirmationService.confirm({
      key: 'workbookdialog',
      message: 'Do you want to delete this Document?',
      header: 'Delete Confirmation',
      icon: 'pi pi-info-circle',
      accept: () => {
        this.deletedDocuments.push({
          id: selectedFile?.id,
          column: column.id,
          index: index
        });
            // Remove file from form UI
            const formArray = this.form.get(column.id) as FormArray;
            formArray.removeAt(index);
            this.form.markAsDirty();
            this.form.markAsTouched();
            this.form.updateValueAndValidity();

            // Show success message
            this.messageService.add({ severity: 'info', summary: 'Info', detail: 'Document will be deleted after saving.' });
      },
      reject: () => { },
    });
  }


  closeModal() {
    this.clearOldValue(); //For encrypted text.
    this.displayModal = false;
    this.closeAddEditModal.emit();
  }

  //#region Tagged User Details Popup
  openTaggedUserDetails(userId: any) {
    this.selectedUser = userId;
    this.displayUserDetails = true;
  }
  closeTaggedUserDetails(){
    this.selectedUser = null;
    this.displayUserDetails = false;
  }

  getSelectedUserName(userId: string) {
    const selectedUser = this.usersList.find(user => user.id === userId);
    return selectedUser?.name || '';
  }

  clearEncryptedText(field: any){
    if(!field?.oldValueCleared&&this.mode === 'edit'){
        field.oldValueCleared = true;
        this.form.get(field.id).setValue("");
    }
}

 clearOldValue(){
  this.columnDefinitions.forEach((field: any) => {
    if (field.hasOwnProperty('oldValueCleared')) {
        delete field.oldValueCleared;
      }
    });
 }
  //#endregion Tagged User Details Popup

  clearRating(columnId, event){
    this.form.get(columnId).setValue(null);
    this.form.markAsDirty();
    this.form.markAsTouched();
    this.form.updateValueAndValidity();
  }
  ngOnDestroy(): void {
    if (this.ngUnsubscribe$) {
      this.ngUnsubscribe$.next();
      this.ngUnsubscribe$.complete();
      this.ngUnsubscribe$.unsubscribe();
    }
  }

  //files and pics
  openFiles(files){
    this.allFiles = files;
    this.displayFiles = true;
  }

  updateStateOptions() {
    if (this.mode === 'add') {
      this.stateOptions = [{label: this.formHeader, value: "view-mode"}];
    } else {
      this.stateOptions = [
        {label: this.formHeader, value: "view-mode"},
        {label: "Comments", value: "comments"}
      ];
    }
    this.selectedOption = "view-mode";
  }
}
